// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $GeoLocation,
  $TrackerPosition,
} from 'common.types';
import type {
  $FullRegattaCourse,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  RegattaCoursePointSide,
  RegattaCoursePointType,
} from 'common.data';
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import {
  GoogleMap,
  LoadScript,
  Marker,
  Polyline,
} from '@react-google-maps/api';
import {
  arrayMove,
  List,
} from 'react-movable';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  DeleteDialog,
  FormDialog,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  calcualteGeoDistance,
  getGeoCenterPosition,
  getMapGeoCenterPosition,
  getMapZoom,
  getRegattaCoursePath,
  getRegattaCoursePathLengths,
} from 'common.helpers/dist/browser';
import {
  regattaCoursePointSideLabel,
  regattaCoursePointTypeLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';
import config from '../../../config';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import GeoDialog from '../../dialog/GeoDialog';
import RegattaCourseFields from '../../fields/RegattaCourseFields';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'RegattaCoursePoint'>>;
  dataTrackerPositions: Array<$TrackerPosition>;
  editOnMap: boolean;
  mainItem: $FullRegattaCourse | null;
  newRegattaCoursePoint: $ModelUpsertParams<'RegattaCoursePoint'> | null;
};

const pageId = adminPages.regattaCoursePage;

class RegattaCoursePageView extends React.Component<$Props, $State> {
  debouncedUpdateRegattaCoursePoint: (
    regattaCoursePoint: $Model<'RegattaCoursePoint'>,
  ) => Promise<void>;

  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      dataTrackerPositions: [],
      editOnMap: false,
      mainItem: null,
      newRegattaCoursePoint: null,
    };

    this.debouncedUpdateRegattaCoursePoint = _.debounce(
      this.updateRegattaCoursePoint,
      2000,
    );
  }

  componentDidMount(): void {
    this.getItem();
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          params,
        } = this.props;
        const {
          RegattaCourseId,
        } = params;

        if (data.modelId === RegattaCourseId && data.modelName === 'RegattaCourse') {
          this.getItem();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });

    Services.Socket.on({
      action: (data) => {
        const {
          params,
        } = this.props;
        const {
          RegattaCourseId,
        } = params;

        if (data.parentModelId === RegattaCourseId && data.modelName === 'RegattaCoursePoint') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getItem(): Promise<void> {
    const {
      params,
    } = this.props;

    const {
      RegattaCourseId,
    } = params;

    await defaultGetData({
      action: async () => {
        if (RegattaCourseId) {
          const mainItem = await Services.Api.getOneRegattaCourse({
            id: RegattaCourseId,
          });

          await this.setStateAsync({
            mainItem,
          });
        }
      },
    });
  }

  async getData(): Promise<void> {
    const {
      params,
    } = this.props;

    const {
      RegattaCourseId,
    } = params;

    await defaultGetData({
      action: async () => {
        if (RegattaCourseId) {
          const data = await Services.Api.getManyRegattaCoursePoint({
            RegattaCourseId,
          });

          await this.setStateAsync({
            data,
          });
        }
      },
    });
  }

  async sortRegattaCoursePoint({
    newIndex,
    oldIndex,
  }: {
    newIndex: number;
    oldIndex: number;
  }): Promise<void> {
    const {
      data,
    } = this.state;

    try {
      const sortedData = arrayMove(
        data,
        oldIndex,
        newIndex,
      );

      await this.setStateAsync({
        data: sortedData,
      });

      const params = {
        items: sortedData.map(({
          id,
        }, index: number) => ({
          id,
          index,
        })),
      };

      await Services.Api.sortRegattaCoursePoint(params);
    } catch (error) {
      await this.setStateAsync({
        data,
      });

      ErrorLog.exception(error as Error);
    }
  }

  /* eslint-disable-next-line class-methods-use-this */
  async updateRegattaCoursePoint(
    regattaCoursePoint: $Model<'RegattaCoursePoint'>,
  ): Promise<void> {
    await defaultUpsertItem({
      action: async () => {
        await Services.Api.upsertRegattaCoursePoint(regattaCoursePoint);
      },
      quiet: true,
    });
  }

  renderHeader() {
    const {
      redirect,
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          description={mainItem !== null ? mainItem.Name : ''}
          rightButtons={mainItem !== null ? (
            <FormDialog
              fields={(
                <RegattaCourseFields item={mainItem} />
              )}
              id={'update-regatta-course-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'RegattaCourse'>>({
                    ...mainItem,
                    ...values,
                  });

                  await Services.Api.upsertRegattaCourse(params);
                },
              })}
              subTitle={t('New')}
              title={t('Regatta course')}
            >
              <Button
                color={'warning'}
                id={`update-regatta-course-button-${mainItem.id}`}
                outline
                size={'sm'}
              >
                {t('Edit')}
              </Button>
            </FormDialog>
          ) : undefined}
          title={t('Regatta course')}
          titleButton={(
            <Button
              className={'mr-2'}
              color={'secondary'}
              id={'redirect-regatta-courses-page-button'}
              onClick={() => redirect({
                pathname: '/regatta/courses',
              })}
              size={'sm'}
            >
              <em className={'fa icon-arrow-left'} />
            </Button>
          )}
        />
      </Card>
    );
  }

  renderMapTrackerPositions() {
    const {
      dataTrackerPositions,
    } = this.state;

    if (dataTrackerPositions.length > 0) {
      const groupedTrackerPositions = _.groupBy(
        dataTrackerPositions,
        'id',
      );

      return _.map(
        groupedTrackerPositions,
        (track: Array<$TrackerPosition>) => (
          <Polyline
            options={{
              strokeColor: 'grey',
              strokeWeight: 0.3,
            }}
            path={track}
          />
        ),
      );
    }

    return null;
  }

  renderMapCourseLabel({
    color,
    position,
    value,
  }: {
    color: string;
    position: $GeoLocation;
    value: string;
  }) {
    const {
      t,
    } = this.props;

    return (
      <Marker
        icon={{
          anchor: {
            equals: () => false,
            x: 0,
            y: 15,
          },
          path: 'M 0 0 z',
        }}
        label={{
          color,
          fontSize: '11px',
          text: value,
        }}
        position={position}
        title={t('Label')}
      />
    );
  }

  renderMapCoursePoint({
    color,
    id,
    label,
    onMove,
    position,
  }: {
    color: string;
    id: string;
    label?: null | string;
    onMove: (arg0: $GeoLocation) => unknown;
    position: $GeoLocation;
  }) {
    const {
      editOnMap,
    } = this.state;

    return (
      <>
        <Marker
          draggable={editOnMap}
          icon={{
            fillColor: color,
            fillOpacity: 0.35,
            path: 'M -5, 0a 5,5 0 1,1 10,0a 5,5 0 1,1 -10,0',
            strokeColor: color,
            strokeOpacity: 0.8,
            strokeWeight: 2,
          }}
          key={id}
          onDragEnd={(mouseEvent) => {
            const {
              latLng,
            } = mouseEvent;

            if (latLng !== null) {
              onMove({
                lat: latLng.lat(),
                lng: latLng.lng(),
              });
            }
          }}
          position={position}
        />
        {label && this.renderMapCourseLabel({
          color: 'grey',
          position,
          value: label,
        })}
      </>
    );
  }

  renderMapCoursePoints() {
    const {
      data,
    } = this.state;

    return (
      <>
        {data.map((dataItem: $Model<'RegattaCoursePoint'>) => {
          if (dataItem.Type === RegattaCoursePointType.gate) {
            const position1: $GeoLocation = {
              lat: dataItem.Lat,
              lng: dataItem.Lng,
            };

            const view = [this.renderMapCoursePoint({
              color: dataItem.Color,
              id: `${dataItem.id}1`,
              label: dataItem.Name,
              onMove: ({
                lat,
                lng,
              }: $GeoLocation) => this.updateRegattaCoursePoint({
                ...dataItem,
                Lat: lat,
                Lng: lng,
              }),
              position: position1,
            })];

            if (dataItem.Lat2 && dataItem.Lng2 && dataItem.Color2) {
              const position2: $GeoLocation = {
                lat: dataItem.Lat2,
                lng: dataItem.Lng2,
              };

              view.push(this.renderMapCoursePoint({
                color: dataItem.Color2,
                id: `${dataItem.id}2`,
                label: dataItem.Name2,
                onMove: ({
                  lat,
                  lng,
                }: $GeoLocation) => this.updateRegattaCoursePoint({
                  ...dataItem,
                  Lat2: lat,
                  Lng2: lng,
                }),
                position: position2,
              }));

              view.push(
                <Polyline
                  options={{
                    icons: [
                      {
                        icon: {
                          path: 'M 0,-1 0,1',
                          scale: 4,
                          strokeOpacity: 1,
                          strokeWeight: 1,
                        },
                        offset: '0',
                        repeat: '20px',
                      },
                    ],
                    strokeColor: 'lightgrey',
                    strokeWeight: 0,
                  }}
                  path={[position1, position2]}
                />,
              );

              view.push(this.renderMapCourseLabel({
                color: 'lightgrey',
                position: getGeoCenterPosition({
                  position1,
                  position2,
                }),
                value: `${calcualteGeoDistance({
                  position1,
                  position2,
                }).toFixed(2)} nm`,
              }));
            }

            return view;
          }

          return this.renderMapCoursePoint({
            color: dataItem.Color,
            id: dataItem.id,
            label: dataItem.Name,
            onMove: ({
              lat,
              lng,
            }: $GeoLocation) => this.updateRegattaCoursePoint({
              ...dataItem,
              Lat: lat,
              Lng: lng,
            }),
            position: {
              lat: dataItem.Lat,
              lng: dataItem.Lng,
            },
          });
        })}
      </>
    );
  }

  renderMapCoursePath(
    path: Array<$GeoLocation>,
    pathLengths: Array<{
      position: $GeoLocation;
      value: number;
    }>,
  ) {
    return (
      <>
        <Polyline
          options={{
            strokeColor: 'grey',
            strokeWeight: 1,
          }}
          path={path}
        />
        {pathLengths.map(({
          position,
          value,
        }) => this.renderMapCourseLabel({
          color: 'grey',
          position,
          value: `${value.toFixed(2)} nm`,
        }))}
      </>
    );
  }

  renderGetTracks() {
    const {
      mainItem,
    } = this.state;

    return (mainItem !== null ? (
      <Input
        name={'RegattaCourseRace'}
        onChange={(e) => {
          const reagattaCourseRace: $Model<'RegattaCourseRace'> | undefined = _.find(
            mainItem.RegattaCourseRaces,
            {
              id: e.target.value,
            },
          );

          if (reagattaCourseRace) {
            defaultGetData({
              action: async () => {
                const params = {
                  from: String(reagattaCourseRace.StartDate),
                  ids: '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25',
                  to: String(reagattaCourseRace.EndDate),
                };

                const newDataTrackerPositions = await Services.Tracker.getPositions(
                  params,
                );

                await this.setStateAsync({
                  dataTrackerPositions: newDataTrackerPositions,
                });
              },
            });
          } else {
            this.setState({
              dataTrackerPositions: [],
            });
          }
        }}
        type={'select'}
      >
        <option />
        {mainItem.RegattaCourseRaces.map((regattaCourseRace: $Model<'RegattaCourseRace'>) => (
          <option
            key={regattaCourseRace.id}
            value={regattaCourseRace.id}
          >
            {regattaCourseRace.Name}
          </option>
        ))}
      </Input>
    ) : null);
  }

  renderMap() {
    const {
      t,
    } = this.props;
    const {
      data,
      editOnMap,
      newRegattaCoursePoint,
    } = this.state;

    const path = getRegattaCoursePath({
      data,
    });

    const pathLengths = getRegattaCoursePathLengths({
      path,
    });

    let centerPosition: $GeoLocation = {
      lat: 54,
      lng: 21,
    };
    let zoom = 15;

    if (data.length > 0) {
      centerPosition = getMapGeoCenterPosition({
        data,
      });
      zoom = getMapZoom({
        data,
      });
    }

    const courseLength: number = _.sumBy(
      pathLengths,
      'value',
    );

    return (
      <>
        <div
          style={{
            borderColor: editOnMap === true ? 'orange' : 'white',
            borderStyle: 'dashed',
            borderWidth: 3,
            height: '50vh',
            width: '100%',
          }}
        >
          <LoadScript googleMapsApiKey={config.GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              center={{
                lat: centerPosition.lat,
                lng: centerPosition.lng,
              }}
              mapContainerStyle={{
                height: '100%',
                width: '100%',
              }}
              onClick={async (
                mouseEvent,
              ) => {
                if (editOnMap === true && newRegattaCoursePoint !== null) {
                  await defaultUpsertItem({
                    action: async () => {
                      const params = newRegattaCoursePoint;

                      const {
                        latLng,
                      } = mouseEvent;

                      if (latLng !== null) {
                        params.Lat = latLng.lat();
                        params.Lng = latLng.lng();

                        await Services.Api.upsertRegattaCoursePoint(params);

                        await this.setStateAsync({
                          editOnMap: false,
                          newRegattaCoursePoint: null,
                        });
                      }
                    },
                  });
                }
              }}
              zoom={zoom}
            >
              {this.renderMapCoursePoints()}
              {this.renderMapCoursePath(
                path,
                pathLengths,
              )}
              {this.renderMapTrackerPositions()}
            </GoogleMap>
          </LoadScript>
        </div>
        <div className={'pt-2'}>
          <Row>
            <Col sm={9}>
              <Label className={'mt-2'}>
                {`${t('Length')}: ${courseLength.toFixed(2)} nm`}
              </Label>
            </Col>
            <Col sm={3}>
              {this.renderGetTracks()}
            </Col>
          </Row>
        </div>
      </>
    );
  }

  renderColorCell(dataItem: $Model<'RegattaCoursePoint'>) {
    const {
      t,
    } = this.props;

    return (
      <>
        <Input
          className={'px-2'}
          name={'color'}
          onChange={(e) => this.updateRegattaCoursePoint({
            ...dataItem,
            Color: e.target.value,
          })}
          title={t('Color')}
          type={'color'}
          value={dataItem.Color}
        />
        {dataItem.Type === RegattaCoursePointType.gate && (
          <Input
            className={'px-2'}
            name={'color2'}
            onChange={(e) => this.updateRegattaCoursePoint({
              ...dataItem,
              Color2: e.target.value,
            })}
            title={t('Color')}
            type={'color'}
            value={dataItem.Color2 || undefined}
          />
        )}
      </>
    );
  }

  renderTypeCell(dataItem: $Model<'RegattaCoursePoint'>) {
    const {
      t,
    } = this.props;

    return (
      <Input
        name={'type'}
        onChange={(e) => defaultUpsertItem({
          action: async () => {
            const value = (_.get(
              e.target,
              'value',
            ) || dataItem.Type) as RegattaCoursePointType;

            const params = {
              ...dataItem,
              Type: value,
            };

            if (params.Type === RegattaCoursePointType.gate
              && dataItem.Lat2 === null
              && dataItem.Lng2 === null
            ) {
              params.Lat2 = params.Lat;
              params.Lng2 = params.Lng + 0.01;
            }

            await Services.Api.upsertRegattaCoursePoint(params);
          },
          quiet: true,
        })}
        title={t('Type')}
        type={'select'}
        value={dataItem.Type}
      >
        {_.map(
          RegattaCoursePointType,
          (v, k) => (
            <option
              key={k}
              value={v}
            >
              {t(regattaCoursePointTypeLabel[v])}
            </option>
          ),
        )}
      </Input>
    );
  }

  renderSideCell(dataItem: $Model<'RegattaCoursePoint'>) {
    const {
      t,
    } = this.props;

    return (
      <Input
        disabled={dataItem.Type === RegattaCoursePointType.gate}
        name={'side'}
        onChange={(e) => {
          const value = (_.get(
            e.target,
            'value',
          ) || dataItem.Side) as RegattaCoursePointSide;

          this.updateRegattaCoursePoint({
            ...dataItem,
            Side: value,
          });
        }}
        title={t('Side')}
        type={'select'}
        value={dataItem.Side}
      >
        {_.map(
          RegattaCoursePointSide,
          (v, k) => (
            <option
              key={k}
              value={v}
            >
              {t(regattaCoursePointSideLabel[v])}
            </option>
          ),
        )}
      </Input>
    );
  }

  renderLocationCell(dataItem: $Model<'RegattaCoursePoint'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <>
        <div
          className={'input-group'}
          title={t('Location')}
        >
          <div className={'form-control lh-1 py-0 d-flex align-items-center'}>
            <small>
              {dataItem.Lat}
              <br />
              {dataItem.Lng}
            </small>
          </div>
          <GeoDialog
            action={(value: $GeoLocation) => this.updateRegattaCoursePoint({
              ...dataItem,
              Lat: value.lat,
              Lng: value.lng,
            })}
            id={`upsert-regatta-course-point1-dialog-${id}`}
            title={dataItem.Name}
            value={{
              lat: dataItem.Lat,
              lng: dataItem.Lng,
            }}
          >
            <Button
              color={'warning'}
              id={`upsert-regatta-course-point1-button-${id}`}
              outline
              size={'sm'}
            >
              <em className={'icon-note'} />
            </Button>
          </GeoDialog>
        </div>
        {(dataItem.Type === RegattaCoursePointType.gate && dataItem.Lat2 && dataItem.Lng2) && (
          <div
            className={'input-group'}
            title={t('Location')}
          >
            <div className={'form-control lh-1 py-0 d-flex align-items-center'}>
              <small>
                {dataItem.Lat2}
                <br />
                {dataItem.Lng2}
              </small>
            </div>
            <GeoDialog
              action={(value: $GeoLocation) => this.updateRegattaCoursePoint({
                ...dataItem,
                Lat2: value.lat,
                Lng2: value.lng,
              })}
              id={`upsert-regatta-course-point2-dialog-${id}`}
              title={dataItem.Name}
              value={{
                lat: dataItem.Lat2,
                lng: dataItem.Lng2,
              }}
            >
              <Button
                color={'warning'}
                id={`upsert-regatta-course-point2-button-${id}`}
                outline
                size={'sm'}
              >
                <em className={'icon-note'} />
              </Button>
            </GeoDialog>
          </div>
        )}
      </>
    );
  }

  renderTrackerCell(dataItem: $Model<'RegattaCoursePoint'>) {
    const {
      t,
    } = this.props;

    return (
      <>
        <Input
          label={t('Tracker ID')}
          min={1}
          name={'tracker-id'}
          onChange={(e) => this.updateRegattaCoursePoint({
            ...dataItem,
            TrackerId: e.target.value ? Number(e.target.value) : null,
          })}
          type={'number'}
          value={dataItem.TrackerId || undefined}
        />
        {dataItem.Type === RegattaCoursePointType.gate && (
          <Input
            label={t('Tracker ID')}
            min={1}
            name={'tracker-id-2'}
            onChange={(e) => this.updateRegattaCoursePoint({
              ...dataItem,
              TrackerId2: e.target.value ? Number(e.target.value) : null,
            })}
            type={'number'}
            value={dataItem.TrackerId2 || undefined}
          />
        )}
      </>
    );
  }

  renderNameCell(dataItem: $Model<'RegattaCoursePoint'>) {
    const {
      t,
    } = this.props;

    return (
      <>
        <Input
          defaultValue={dataItem.Name}
          label={t('Title')}
          name={'name'}
          onChange={(e) => this.debouncedUpdateRegattaCoursePoint({
            ...dataItem,
            Name: e.target.value,
          })}
          type={'text'}
        />
        {dataItem.Type === RegattaCoursePointType.gate && (
          <Input
            defaultValue={dataItem.Name2 || undefined}
            label={t('Title')}
            name={'name2'}
            onChange={(e) => this.debouncedUpdateRegattaCoursePoint({
              ...dataItem,
              Name2: e.target.value,
            })}
            type={'text'}
          />
        )}
      </>
    );
  }

  renderButtonsCell(dataItem: $Model<'RegattaCoursePoint'>) {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'info'}
          id={`clone-regatta-course-point-button-${dataItem.id}`}
          onClick={() => defaultUpsertItem({
            action: () => Services.Api.upsertRegattaCoursePoint({
              ...dataItem,
              id: undefined,
              SortId: data.length,
            }),
          })}
          outline
          size={'sm'}
        >
          {t('Clone')}
        </Button>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-course-point-dialog-${dataItem.id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaCoursePoint(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta course')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-course-point-button-${dataItem.id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderContentMap() {
    const {
      t,
    } = this.props;
    const {
      editOnMap,
    } = this.state;

    return (
      <Card id={adminSections[pageId].map}>
        <SectionHeader
          rightButtons={(editOnMap === true ? (
            <Button
              color={'warning'}
              onClick={() => this.setState({
                editOnMap: false,
                newRegattaCoursePoint: null,
              })}
              size={'sm'}
            >
              {t('Stop edit on map')}
            </Button>
          ) : (
            <Button
              color={'warning'}
              onClick={() => this.setState({
                editOnMap: true,
              })}
              outline
              size={'sm'}
            >
              {t('Edit on map')}
            </Button>
          ))}
          title={t('Regatta course map')}
        />
        <CardBody>
          {this.renderMap()}
        </CardBody>
      </Card>
    );
  }

  renderContentList() {
    const {
      t,
    } = this.props;
    const {
      data,
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      const newRegattaCoursePoint: $ModelUpsertParams<'RegattaCoursePoint'> = {
        Color: '#FF0000',
        Color2: null,
        Lat: 0,
        Lat2: null,
        Lng: 0,
        Lng2: null,
        Name: 'Mark',
        Name2: null,
        RegattaCourseId: mainItem.id,
        Side: RegattaCoursePointSide.port,
        SortId: data.length,
        TrackerId: null,
        TrackerId2: null,
        Type: RegattaCoursePointType.default,
      };

      return (
        <Card id={adminSections[pageId].list}>
          <SectionHeader
            rightButtons={(
              <Button
                color={'info'}
                id={'create-regatta-course-point-button'}
                onClick={() => this.setState({
                  editOnMap: true,
                  newRegattaCoursePoint,
                })}
                size={'sm'}
                title={t('Add new regatta course point')}
              >
                {t('Add new')}
              </Button>
            )}
            title={t('Regatta course points')}
          />
          <CardBody>
            {data.length === 0 ? <EmptyTable className={'mt-5'} /> : (
              <Table responsive>
                <thead>
                  <tr>
                    <th />
                    <th
                      style={{
                        width: 60,
                      }}
                    >
                      {t('Color')}
                    </th>
                    <th>{t('Type')}</th>
                    <th>{t('Side')}</th>
                    <th>{t('Location')}</th>
                    <th
                      style={{
                        width: 100,
                      }}
                    >
                      {t('Tracker ID')}
                    </th>
                    <th>{t('Name')}</th>
                    <th />
                  </tr>
                </thead>
                <List
                  onChange={async ({
                    newIndex,
                    oldIndex,
                  }) => this.sortRegattaCoursePoint({
                    newIndex,
                    oldIndex,
                  })}
                  renderItem={({
                    props,
                    value,
                  }) => (
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    <tr {...props}>
                      <td>
                        <em className={'icon-menu'} />
                      </td>
                      <td>
                        {this.renderColorCell(value)}
                      </td>
                      <td>
                        {this.renderTypeCell(value)}
                      </td>
                      <td>
                        {this.renderSideCell(value)}
                      </td>
                      <td>
                        {this.renderLocationCell(value)}
                      </td>
                      <td>
                        {this.renderTrackerCell(value)}
                      </td>
                      <td>
                        {this.renderNameCell(value)}
                      </td>
                      <td>
                        {this.renderButtonsCell(value)}
                      </td>
                    </tr>
                  )}
                  renderList={({
                    children,
                    props: {
                      ref,
                    },
                  }) => (
                    <tbody ref={ref}>
                      {children}
                    </tbody>
                  )}
                  values={data}
                />
              </Table>
            )}
          </CardBody>
        </Card>
      );
    }

    return null;
  }

  renderContent() {
    return (
      <>
        {this.renderContentMap()}
        {this.renderContentList()}
      </>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default RegattaCoursePageView;
